import { useState, useMemo } from "react";
import { Profile } from "@/hooks/profile/types";
import { useProfessorSchedules, ProfessorScheduleEntry } from "@/hooks/schedules/useProfessorSchedules";
import { useScheduleChangeRequests } from "@/hooks/schedules/useScheduleChangeRequests";
import { useProfessorComments } from "@/hooks/schedules/useProfessorComments";
import { ScheduleCard } from "./ScheduleCard";
import { ChangeRequestList } from "./ChangeRequestList";
import { ProfessorCommentForm } from "./ProfessorCommentForm";
import { ProfessorCommentsList } from "./ProfessorCommentsList";
import { Loader2, Users, Calendar, MessageSquare } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { startOfWeek, endOfWeek, isWithinInterval, getDay } from "date-fns";

const DAY_NAMES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

const parseLocalDate = (dateStr: string) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

interface Props {
  profile: Profile;
}

export const ProfesorScheduleView = ({ profile }: Props) => {
  const { data: schedules, isLoading: schedulesLoading } = useProfessorSchedules(profile.gym_id);
  const { requests, resolveRequest, isLoading: requestsLoading } = useScheduleChangeRequests(profile.gym_id, "professor");
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const { comments, addComment, isLoading: commentsLoading } = useProfessorComments(selectedMember || undefined, profile.gym_id);

  const isLoading = schedulesLoading || requestsLoading;

  // Build map of approved changes for current week
  const activeChangesMap = useMemo(() => {
    const map = new Map<string, typeof requests[0]>();
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

    requests
      .filter(r => r.status === "approved" && r.replacement_date)
      .forEach(r => {
        const replDate = parseLocalDate(r.replacement_date!);
        if (isWithinInterval(replDate, { start: weekStart, end: weekEnd })) {
          map.set(r.schedule_id, r);
        }
      });
    return map;
  }, [requests]);

  // Transform schedules: move entries with active changes to their replacement day
  const effectiveSchedules = useMemo(() => {
    return (schedules || []).map(s => {
      const change = activeChangesMap.get(s.id);
      if (!change || !change.replacement_date) return { ...s, _isTemporary: false };
      return {
        ...s,
        day_of_week: getDay(parseLocalDate(change.replacement_date)),
        start_time: change.requested_start_time,
        end_time: change.requested_end_time,
        _isTemporary: true,
      };
    });
  }, [schedules, activeChangesMap]);

  // Group schedules by day
  const groupedByDay = effectiveSchedules.reduce((acc, s) => {
    const key = s.day_of_week;
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {} as Record<number, (ProfessorScheduleEntry & { _isTemporary: boolean })[]>);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!schedules || schedules.length === 0) {
    return (
      <div className="text-center py-12 space-y-3 animate-fade-in">
        <Users className="w-12 h-12 mx-auto text-muted-foreground/50" />
        <p className="text-muted-foreground">No tenés turnos asignados.</p>
      </div>
    );
  }

  const pendingRequests = requests.filter(r => r.status === "pending");

  return (
    <div className="space-y-6 animate-fade-in px-1">
      {/* Pending change requests */}
      {pendingRequests.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Solicitudes pendientes ({pendingRequests.length})
          </h2>
          <ChangeRequestList
            requests={pendingRequests}
            mode="professor"
            onResolve={(id, status) => resolveRequest.mutate({ id, status })}
          />
        </section>
      )}

      {/* Schedules grouped by day */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Mis turnos y socios
        </h2>
        {Object.entries(groupedByDay)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([day, entries]) => (
            <Card key={day} className="overflow-hidden">
              <CardHeader className="py-3 px-4 bg-primary/5">
                <CardTitle className="text-sm font-medium">{DAY_NAMES[Number(day)]}</CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {entries.map(entry => (
                  <Collapsible key={entry.id}>
                    <CollapsibleTrigger className="w-full" onClick={() => setSelectedMember(entry.member_id)}>
                      <div className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={entry.member?.avatar_url || ""} />
                            <AvatarFallback className="text-xs bg-primary/10 text-primary">
                              {(entry.member?.first_name?.[0] || "") + (entry.member?.last_name?.[0] || "")}
                            </AvatarFallback>
                          </Avatar>
                           <div className="text-left">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-foreground">
                                {entry.member?.first_name} {entry.member?.last_name}
                              </p>
                              {entry._isTemporary && (
                                <Badge className="text-[10px] px-1.5 py-0 bg-green-500/10 text-green-600 border-green-500/20">
                                  Cambio temporal
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {entry.start_time.slice(0, 5)} - {entry.end_time.slice(0, 5)}
                            </p>
                          </div>
                        </div>
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-2 pb-2 space-y-3">
                      <div className="border-t pt-3">
                        <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> Comentarios
                        </h4>
                        {selectedMember === entry.member_id && (
                          <>
                            <ProfessorCommentForm
                              memberId={entry.member_id}
                              gymId={profile.gym_id!}
                              scheduleId={entry.id}
                              onSubmit={addComment.mutate}
                              isPending={addComment.isPending}
                            />
                            {!commentsLoading && (
                              <ProfessorCommentsList comments={comments} compact />
                            )}
                          </>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </CardContent>
            </Card>
          ))}
      </section>

      {/* All change requests */}
      {requests.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Historial de solicitudes</h2>
          <ChangeRequestList requests={requests} mode="professor" onResolve={(id, status) => resolveRequest.mutate({ id, status })} />
        </section>
      )}
    </div>
  );
};
